import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState, useMemo, useRef, useEffect } from 'react';

import { SmallTooltip } from '../../Tooltip';
import { useTheme } from 'styled-components';
import styled from 'styled-components';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { TOPIC_TO_COLOR } from '../../../theme';
import { TopicPill, TopicIcon, TopicText } from '../TopicPill';
import autoAnimate from '@formkit/auto-animate';

import {
  useGetCompany,
  useGetExerciseSubmissions,
} from '../../../hooks/use-hydra/admin';
import { useHistory, useParams } from 'react-router-dom';
import { SubmissionResponse } from '../../../api/admin';
import SOSQuestionsFilters from './SOSQuestionsFilters';
import { P } from '../../elements/Text';
import Table from '../../Table/CompactTable';
import { ROUTES } from '../../../routing';

dayjs.extend(relativeTime);

const TableWrapper = styled.div`
  min-height: 64vh;
  > div {
    max-height: 64vh;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    > div {
      border-radius: 8px;
    }
  }
  .sos-questions-table {
    th {
      border-top: none !important;
    }
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }
  }
`;

const SubmissionsTable = ({
  submissions,
}: {
  submissions: SubmissionResponse['submissions'];
}) => {
  const { id } = useParams<{ id: string }>();

  const { data: companyData } = useGetCompany(parseInt(id));
  const history = useHistory();
  const theme = useTheme();
  const [statusFilter, setStatusFilter] =
    useState<'All' | 'Replied' | 'Not Replied'>('Not Replied');
  const [mentorFilter, setMentorFilter] = useState<string[]>([]);
  const [exerciseFilter, setExerciseFilter] = useState<string>('');

  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    if (tableBodyRef.current) {
      autoAnimate(tableBodyRef.current);
    }
  }, []);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((submission) => {
      // Status filter
      if (statusFilter !== 'All') {
        const isReviewed = submission.submission.isReviewed;
        if (statusFilter === 'Replied' && !isReviewed) return false;
        if (statusFilter === 'Not Replied' && isReviewed) return false;
      }

      // Mentor filter
      if (mentorFilter.length > 0) {
        const learnerId = submission.learner.id;
        const latestCohortLearner = companyData?.cohorts
          .flatMap((cohort) => cohort.learners)
          .filter((learner) => learner.learnerId === learnerId);

        const mentorName = latestCohortLearner?.[0]?.mentor?.name;
        if (!mentorName || !mentorFilter.includes(mentorName)) return false;
      }

      // Exercise filter
      if (exerciseFilter) {
        const lessonTitle = submission.unit.title;
        const exerciseNumber = submission.question.index + 1;
        const exerciseName = `${lessonTitle} - Exercise #${exerciseNumber}`;
        if (!exerciseName.toLowerCase().includes(exerciseFilter.toLowerCase()))
          return false;
      }

      return true;
    });
  }, [submissions, statusFilter, mentorFilter, exerciseFilter, companyData]);

  const columnHelper =
    createColumnHelper<SubmissionResponse['submissions'][number]>();

  const openSubmission = (submissionId: number, questionId: number) => {
    history.push(
      ROUTES.ADMIN.REPLY_TO_SOS_QUESTION.getPath({
        companyId: id,
        submissionId: submissionId.toString(),
        questionId: questionId.toString(),
      })
    );
  };

  const columns: Array<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ColumnDef<SubmissionResponse['submissions'][number], any>
  > = [
    columnHelper.accessor('learner.name', {
      header: 'Learner',
      id: 'learner.name',
      cell: (info) => {
        const uniqueId = `${info.row.original.submission.id}-${info.row.original.question.id}`;
        return (
          <div
            className={`learner-${uniqueId}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {info.getValue() ?? 'N/A'}
            <SmallTooltip anchorSelect={`.learner-${uniqueId}`}>
              {info.row.original.learner.email}
            </SmallTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '150px',
        },
      },
    }),
    columnHelper.display({
      id: 'mentor',
      header: 'Mentor',
      cell: (info) => {
        const learnerId = info.row.original.learner.id;
        const latestCohortLearner = companyData?.cohorts
          .flatMap((cohort) => cohort.learners)
          .filter((learner) => learner.learnerId === learnerId);

        const uniqueId = `${info.row.original.submission.id}-${info.row.original.question.id}`;

        const mentor = latestCohortLearner?.[0]?.mentor;
        return (
          <div
            className={`mentor-${uniqueId}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {mentor?.name ?? 'N/A'}
            <SmallTooltip anchorSelect={`.mentor-${uniqueId}`}>
              {mentor?.email}
            </SmallTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '150px',
        },
      },
    }),
    columnHelper.accessor('unit.topic', {
      header: 'Topic',
      cell: (info) => {
        const topicColor =
          TOPIC_TO_COLOR[info.getValue() as keyof typeof TOPIC_TO_COLOR] ||
          theme.colors.primaryBg;
        const iconUrl = info.row.original.unit.iconUrl;

        return (
          <TopicPill backgroundColor={topicColor}>
            {iconUrl && <TopicIcon src={iconUrl} alt="icon" />}
            <TopicText>{info.getValue()}</TopicText>
          </TopicPill>
        );
      },
      meta: {
        styles: {
          minWidth: '100px',
        },
      },
    }),
    columnHelper.display({
      id: 'lesson',
      header: 'Lesson',
      cell: (info) => {
        const lessonTitle = info.row.original.unit.title;
        const exerciseNumber = info.row.original.question.index + 1;
        return `${lessonTitle} - Exercise #${exerciseNumber}`;
      },
      meta: {
        styles: {
          minWidth: '350px',
        },
      },
    }),

    columnHelper.display({
      id: 'comment',
      header: 'Comment',
      cell: (info) => {
        const uniqueId = `${info.row.original.submission.id}-${info.row.original.question.id}`;
        const comment = info.row.original.submission.learnerComment ?? '';
        const truncatedComment =
          comment.length > 20 ? comment.substring(0, 20) + '...' : comment;
        return (
          <div className={`comment-${uniqueId}`}>
            {truncatedComment}
            <SmallTooltip anchorSelect={`.comment-${uniqueId}`}>
              {comment}
            </SmallTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '200px',
        },
      },
    }),

    columnHelper.accessor('submission.requestedAt', {
      header: 'Requested At',
      cell: (info) => {
        const requestedAt = info.getValue();
        return requestedAt ? dayjs(requestedAt).fromNow() : 'N/A';
      },
      meta: {
        styles: {
          minWidth: '150px',
        },
      },
    }),
    columnHelper.display({
      id: 'replied-by',
      header: 'Replied By',
      cell: (info) => {
        const repliedBy = info.row.original.submission.repliedBy;

        if (!repliedBy) {
          return '-';
        }

        return (
          <div
            className={`replied-by-${info.row.original.submission.id}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {repliedBy?.name ?? 'N/A'}
            <SmallTooltip
              anchorSelect={`.replied-by-${info.row.original.submission.id}`}
            >
              {repliedBy?.email}
            </SmallTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '150px',
        },
      },
    }),
    columnHelper.accessor('submission.isReviewed', {
      header: 'Status',
      cell: (info) => (
        <span
          style={{
            backgroundColor: info.getValue()
              ? theme.colors.elementBgSuccess
              : theme.colors.elementBgDanger,
            padding: '4px 8px',
            borderRadius: '12px',
            color: info.getValue()
              ? theme.colors.textSuccess
              : theme.colors.textDanger,
            whiteSpace: 'nowrap',
          }}
        >
          {info.getValue() ? 'Replied' : 'Not Replied'}
        </span>
      ),
      meta: {
        styles: {
          minWidth: '100px',
        },
      },
    }),
  ];

  return (
    <div>
      <SOSQuestionsFilters
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        mentorFilter={mentorFilter}
        setMentorFilter={setMentorFilter}
        mentors={
          companyData?.mentors.filter((mentor) => mentor.isCompanyMentor) || []
        }
        exerciseFilter={exerciseFilter}
        setExerciseFilter={setExerciseFilter}
      />
      {filteredSubmissions.length === 0 && (
        <P style={{ fontStyle: 'italic' }}>No questions found.</P>
      )}
      {filteredSubmissions.length > 0 && (
        <TableWrapper>
          <Table
            shouldPinHeaderRow
            className="sos-questions-table"
            data={filteredSubmissions}
            columns={columns}
            initialPinning={{
              left: ['learner.name', 'mentor'],
            }}
            getRowId={(row) => `${row.submission.id}-${row.question.id}`}
            getRowProps={(row) => ({
              onClick: () =>
                openSubmission(
                  row.original.submission.id,
                  row.original.question.id
                ),
            })}
          />
        </TableWrapper>
      )}
    </div>
  );
};

export default function SOSQuestions({
  submissions,
}: {
  submissions: SubmissionResponse['submissions'];
}) {
  const { id } = useParams<{ id: string }>();

  const { data: submissionsData } = useGetExerciseSubmissions(parseInt(id));

  return (
    <div>
      <SubmissionsTable
        submissions={submissions || submissionsData?.submissions || []}
      />
    </div>
  );
}
